import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from "axios";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://boutika.helektrika.com/api";

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || "10000"),
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Enable cookies
    });

    // Add request interceptor for auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - just clear token, don't redirect
          this.clearToken();
        }
        return Promise.reject(error);
      },
    );
  }

  private getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(
      process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
    );
  }

  private clearToken(): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(
      process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
    );
    localStorage.removeItem(
      process.env.NEXT_PUBLIC_AUTH_REFRESH_KEY || "refresh_token",
    );
  }

  public setToken(token: string): void {
    if (typeof window === "undefined") return;
    localStorage.setItem(
      process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "auth_token",
      token,
    );
  }

  // Generic request method
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client<T>(config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // GET request
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  // POST request
  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  // PUT request
  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  // PATCH request
  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  // DELETE request
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      return new Error(message);
    }
    return error;
  }
}

export const apiClient = new ApiClient();
